import React, { useState } from 'react';
import Layout from 'layout/Layout';
import { useGetAllMembersQuery } from 'api/consultancyApi';
import { useGetAllUsersQuery } from 'api/userApi';
import { useGetAllProductsQuery } from 'api/productApi';
import { useGetAllWorkshopsQuery } from 'api/workshopApi';
import { useGetAllSurveysQuery } from 'api/surveyApi';
import { useUser } from 'context/UserContext';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Users, ShoppingBag, Presentation, Users2, ClipboardList } from 'lucide-react';

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const { name } = useUser();
  const { data: usersData, isLoading: usersLoading, isError: usersError } = useGetAllUsersQuery();
  const { data: productsData, isLoading: productsLoading, isError: productsError } = useGetAllProductsQuery();
  const { data: workshopsData, isLoading: workshopsLoading, isError: workshopsError } = useGetAllWorkshopsQuery();
  const { data: membersData, isLoading: membersLoading, isError: membersError } = useGetAllMembersQuery();
  const { data: surveysData, isLoading: surveysLoading, isError: surveysError } = useGetAllSurveysQuery();

  const usersCount = Array.isArray(usersData) ? usersData.length : 0;
  const productsCount = Array.isArray(productsData) ? productsData.length : 0;
  const workshopsCount = workshopsData?.data?.length ?? 0;
  const membersCount = membersData?.data?.length ?? 0;
  const surveysCount = Array.isArray(surveysData) ? surveysData.length : (surveysData?.data?.length ?? 0);

  const [timeFilter, setTimeFilter] = useState('month');
  const handleFilterChange = (filter) => setTimeFilter(filter);

  const getLineChartData = () => {
    const dataByFilter = {
      day: {
        labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        users: [10, 15, 20, 12, 18, 25, 22],
        products: [5, 8, 6, 7, 9, 11, 10],
        workshops: [2, 3, 2, 1, 4, 5, 3],
      },
      month: {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
        users: [80, 60, 95, 85, 90, 70, 100],
        products: [40, 30, 50, 45, 48, 55, 60],
        workshops: [15, 20, 18, 22, 19, 25, 30],
      },
      year: {
        labels: ['2019', '2020', '2021', '2022', '2023'],
        users: [400, 600, 900, 1200, 1500],
        products: [200, 300, 450, 600, 750],
        workshops: [50, 70, 90, 110, 130],
      },
    };

    const current = dataByFilter[timeFilter];

    return {
      labels: current.labels,
      datasets: [
        {
          label: 'Users',
          data: current.users,
          borderColor: 'rgba(99, 102, 241, 1)',
          backgroundColor: 'rgba(99, 102, 241, 0.2)',
          tension: 0.3,
          fill: true,
        },
        {
          label: 'Products',
          data: current.products,
          borderColor: 'rgba(59, 130, 245, 1)',
          backgroundColor: 'rgba(59, 130, 245, 0.5)',
          tension: 0.3,
          fill: true,
        },
        {
          label: 'Workshops',
          data: current.workshops,
          borderColor: 'rgba(251, 191, 36, 1)',
          backgroundColor: 'rgba(251, 191, 36, 0.2)',
          tension: 0.3,
          fill: true,
        },
      ],
    };
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
    },
    scales: { y: { beginAtZero: true } },
  };

  if (usersLoading || productsLoading || workshopsLoading || membersLoading || surveysLoading) {
    return (
      <Layout title="Dashboard" tagline="All You Need, In One Place">
        <div className="p-6 text-center text-gray-500">Loading dashboard data...</div>
      </Layout>
    );
  }

  if (usersError || productsError || workshopsError || membersError || surveysError) {
    console.error("Dashboard API Errors:", { usersError, productsError, workshopsError, membersError, surveysError });
  }

  return (
    <Layout title="Dashboard" tagline="All You Need, In One Place">
      <div className="px-4 md:px-6 mt-14 lg:px-10 py-6 space-y-8">
        
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Welcome, {name || 'Admin'}!</h1>
          <p className="text-gray-500 mt-1">Here is the overview of your system.</p>
        </div>

        {/* Top Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          <Card title="Workshops" value={workshopsCount.toLocaleString()} icon={<Presentation className="w-8 h-8 sm:w-10 sm:h-10 text-white" />} color="bg-yellow-400" />
          <Card title="Team" value={membersCount.toLocaleString()} icon={<Users2 className="w-8 h-8 sm:w-10 sm:h-10 text-white" />} color="bg-red-500" />
          <Card title="Products" value={productsCount.toLocaleString()} icon={<ShoppingBag className="w-8 h-8 sm:w-10 sm:h-10 text-white" />} color="bg-blue-500" />
          <Card title="Surveys" value={surveysCount.toLocaleString()} icon={<ClipboardList className="w-8 h-8 sm:w-10 sm:h-10 text-white" />} color="bg-green-500" />
          <Card title="Users" value={usersCount.toLocaleString()} icon={<Users className="w-8 h-8 sm:w-10 sm:h-10 text-white" />} color="bg-violet-600" />
        </div>

        {/* Traffic Chart */}
        <div className="bg-white rounded-2xl shadow-md p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4">
            <div>
              <h2 className="text-lg sm:text-2xl font-semibold text-gray-800">Traffic</h2>
              <p className="text-sm text-gray-500 capitalize">
                {timeFilter === 'day' && 'This Week'}
                {timeFilter === 'month' && 'January - July 2023'}
                {timeFilter === 'year' && '2019 - 2023'}
              </p>
            </div>
            <div className="flex gap-2 mt-2 sm:mt-0">
              {['day', 'month', 'year'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => handleFilterChange(filter)}
                  className={`px-3 py-1 text-sm rounded ${
                    timeFilter === filter ? 'bg-gray-800 text-white' : 'bg-gray-200'
                  }`}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="relative w-full h-[250px] sm:h-[350px] md:h-[400px]">
            <Line data={getLineChartData()} options={lineOptions} />
          </div>
        </div>
      </div>
    </Layout>
  );
};

const Card = ({ title, value, color, icon }) => (
  <div className={`${color} text-white rounded-2xl p-4 sm:p-6 shadow-lg flex flex-col justify-between`}>
    <div className="flex items-center justify-between">
      <div>
        <h3 className="text-2xl sm:text-4xl font-bold">{value}</h3>
        <p className="text-sm sm:text-lg mt-2">{title}</p>
      </div>
      <div className="ml-4">{icon}</div>
    </div>
  </div>
);

export default Dashboard;
